// Settings directory and focused preference pages; existing persistence remains in its owning APIs.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Database, Globe2, Info, LogOut, Plug, SlidersHorizontal, Tags, Users, Wallet } from 'lucide-react';
import { useUserPreferences } from '@/lib/preferences';
import { PageLayout, PageLink } from '@/components/PageLayout';
import { Segmented } from '@/components/ui/Segmented';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { signOut } from '@/lib/auth-client';
import { useWorkspaceStore } from '@/stores/workspace';
import { NotificationsSection } from '@/components/settings/NotificationsSection';
import { useTheme, type Theme } from '@/hooks/useTheme';

type BudgetPeriod = 'weekly' | 'fortnightly' | 'monthly';
type PayFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'yearly';
type Country = 'AU' | 'US' | 'UK' | 'CN' | 'other';

interface HouseholdProfileResponse {
  household_size: number | null;
  current_savings_aud: number | null;
  notes: string | null;
  budget_period: BudgetPeriod;
  pay_anchor_date: string | null;
  country: string;
  fy_start_month: number;
  default_pay_frequency: PayFrequency | null;
  default_pay_day_of_week: number | null;
}

interface ProfileDraft {
  country: Country;
  fy_start_month: number;
  default_pay_frequency: PayFrequency | null;
  default_pay_day_of_week: number | null;
  budget_period: BudgetPeriod;
  pay_anchor_date: string | null;
}

/** Per-country presets — applied when user picks a country in the picker.
 *  User can still override individual fields after, but the defaults match
 *  the local norm so most setups are 0-click after country selection. */
const COUNTRY_DEFAULTS: Record<Country, Omit<ProfileDraft, 'country'>> = {
  AU: { fy_start_month: 7, default_pay_frequency: 'weekly', default_pay_day_of_week: 4, budget_period: 'weekly', pay_anchor_date: null },
  US: { fy_start_month: 1, default_pay_frequency: 'fortnightly', default_pay_day_of_week: 5, budget_period: 'monthly', pay_anchor_date: null },
  UK: { fy_start_month: 4, default_pay_frequency: 'monthly', default_pay_day_of_week: null, budget_period: 'monthly', pay_anchor_date: null },
  CN: { fy_start_month: 1, default_pay_frequency: 'monthly', default_pay_day_of_week: null, budget_period: 'monthly', pay_anchor_date: null },
  other: { fy_start_month: 1, default_pay_frequency: null, default_pay_day_of_week: null, budget_period: 'monthly', pay_anchor_date: null },
};

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  const ws = useWorkspaceStore((s) => s.current);
  return (
    <PageLayout title={t('settings.title')} subtitle={t('settings.subtitle')} backTo="/home">
      <section className="space-y-2" aria-label={zh ? '家庭与偏好' : 'Household and preferences'}>
        <h2 className="px-1 text-xs font-medium text-muted-foreground">{zh ? '家庭与偏好' : 'Household and preferences'}</h2>
        <PageLink to="/workspace" icon={Users} title={t('settings.workspace')} description={ws?.name || t('settings.workspaceHint')} />
        <PageLink to="/settings/profile" icon={Wallet} title={t('settings.profileTitle')} description={zh ? '国家、财年、发薪与预算周期' : 'Country, financial year, pay and budget cadence'} />
        <PageLink to="/settings/appearance" icon={Globe2} title={zh ? '外观与语言' : 'Appearance and language'} description={t('settings.translateMerchantsHint')} />
        <PageLink to="/settings/notifications" icon={Bell} title={zh ? '通知与提醒' : 'Notifications and reminders'} description={zh ? '推送、静默时间和设备' : 'Push delivery, quiet hours and devices'} />
      </section>
      <section className="space-y-2" aria-label={zh ? '数据与连接' : 'Data and connections'}>
        <h2 className="px-1 text-xs font-medium text-muted-foreground">{zh ? '数据与连接' : 'Data and connections'}</h2>
        <PageLink to="/settings/data" icon={Database} title={zh ? '数据整理' : 'Data organization'} description={zh ? '分类与商家、商品别名' : 'Categories, merchant and item aliases'} />
        <PageLink to="/settings/integrations" icon={Plug} title={t('integrations.pageTitle')} description="PricesAPI" />
        <PageLink to="/settings/mcp" icon={SlidersHorizontal} title={zh ? 'AI / MCP 连接' : 'AI / MCP connections'} description={zh ? '管理授权，恢复已排除的账单' : 'Manage access and restore excluded invoices'} />
      </section>
      <PageLink to="/settings/about" icon={Info} title={zh ? '关于与账号' : 'About and account'} description={`Spent Tracker · v${__APP_VERSION__}`} />
    </PageLayout>
  );
}

export function SettingsProfilePage() {
  const ws = useWorkspaceStore((s) => s.current);
  return <SettingsProfileForm key={ws?.id ?? 'no-workspace'} />;
}

// A workspace switch starts a fresh draft before any household preferences can be saved.
function SettingsProfileForm() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const ws = useWorkspaceStore((s) => s.current);
  const qc = useQueryClient();
  const householdQuery = useQuery({
    queryKey: ['advisor', 'household-profile', ws?.id],
    queryFn: () => api.get<HouseholdProfileResponse>('/advisor/household-profile'),
    enabled: !!ws,
    staleTime: 60 * 1000,
  });
  useEffect(() => {
    const profile = householdQuery.data;
    if (!profile) return;
    setDraft((current) => current ?? {
      country: (profile.country as Country) ?? 'AU',
      fy_start_month: profile.fy_start_month ?? 7,
      default_pay_frequency: profile.default_pay_frequency,
      default_pay_day_of_week: profile.default_pay_day_of_week,
      budget_period: profile.budget_period ?? 'weekly',
      pay_anchor_date: profile.pay_anchor_date,
    });
  }, [householdQuery.data]);
  const saveProfile = useMutation({
    mutationFn: (next: ProfileDraft) => api.put<HouseholdProfileResponse>('/advisor/household-profile', {
      country: next.country,
      fy_start_month: next.fy_start_month,
      default_pay_frequency: next.default_pay_frequency,
      default_pay_day_of_week: next.default_pay_day_of_week,
      budget_period: next.budget_period,
      pay_anchor_date: next.budget_period === 'fortnightly' ? next.pay_anchor_date : null,
    }),
    onSuccess: (res) => {
      qc.setQueryData(['advisor', 'household-profile', ws?.id], res);
      qc.invalidateQueries({ queryKey: ['insights'] });
      qc.invalidateQueries({ queryKey: ['advisor'] });
      toast.success(t('common.saved'));
    },
    onError: (err) => toast.error(t('common.saveFailed', { error: err instanceof Error ? err.message : '?' })),
  });
  function applyCountryPreset(country: Country) {
    setDraft((d) => (d ? { country, ...COUNTRY_DEFAULTS[country] } : d));
  }
  return (
    <PageLayout title={t('settings.profilePickerTitle')} subtitle={t('settings.profilePickerDesc')} backTo="/settings">
      {householdQuery.isLoading && <p role="status" className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {householdQuery.isError && <div role="alert" className="rounded-xl border bg-card p-4 text-sm"><p>{householdQuery.error.message}</p><button type="button" onClick={() => void householdQuery.refetch()} className="mt-2 min-h-[44px] rounded-lg border px-4">{t('common.retry')}</button></div>}
      <div className="rounded-2xl border bg-card p-4">
        {draft && (
          <div className="space-y-4">
            {/* 1. Country — picking this auto-fills the rest */}
            <div>
              <div className="text-xs text-muted-foreground">{t('settings.profileCountry')}</div>
              <Segmented<Country>
                value={draft.country}
                onChange={applyCountryPreset}
                options={(['AU', 'US', 'UK', 'CN', 'other'] as Country[]).map((c) => ({
                  value: c,
                  label: countryLabel(c, t),
                }))}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                {t('settings.profileCountryHint')}
              </div>
            </div>

            {/* 2. Financial year start */}
            <div>
              <div className="text-xs text-muted-foreground">{t('settings.profileFyStart')}</div>
              <Segmented<string>
                value={String(draft.fy_start_month)}
                onChange={(v) => setDraft({ ...draft, fy_start_month: Number(v) })}
                options={['1', '4', '7', '10'].map((m) => ({
                  value: m,
                  label: t('settings.profileFyOption', { month: m }),
                }))}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                {t('settings.profileFyHint')}
              </div>
            </div>

            {/* 3. Default pay frequency + day-of-week */}
            <div>
              <div className="text-xs text-muted-foreground">{t('settings.profilePayFreq')}</div>
              <Segmented<string>
                value={draft.default_pay_frequency ?? 'none'}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    default_pay_frequency: v === 'none' ? null : (v as PayFrequency),
                    default_pay_day_of_week:
                      v === 'weekly' || v === 'fortnightly'
                        ? draft.default_pay_day_of_week ?? 4
                        : null,
                  })
                }
                options={[
                  { value: 'none', label: t('settings.profilePayFreqNone') },
                  { value: 'weekly', label: t('advisor.freqWeekly') },
                  { value: 'fortnightly', label: t('advisor.freqFortnightly') },
                  { value: 'monthly', label: t('advisor.freqMonthly') },
                ]}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                {t('settings.profilePayFreqHint')}
              </div>
            </div>

            {(draft.default_pay_frequency === 'weekly' ||
              draft.default_pay_frequency === 'fortnightly') && (
              <div>
                <div className="text-xs text-muted-foreground">{t('settings.profilePayDay')}</div>
                <Segmented<string>
                  value={String(draft.default_pay_day_of_week ?? 4)}
                  onChange={(v) =>
                    setDraft({ ...draft, default_pay_day_of_week: Number(v) })
                  }
                  options={['1', '2', '3', '4', '5', '6', '7'].map((d) => ({
                    value: d,
                    label: dowLabel(Number(d), t),
                  }))}
                  className="mt-1"
                />
              </div>
            )}

            {/* 4. Budget period (existing) */}
            <div>
              <div className="text-xs text-muted-foreground">
                {t('settings.budgetPeriod')}
              </div>
              <Segmented<BudgetPeriod>
                value={draft.budget_period}
                onChange={(p) => setDraft({ ...draft, budget_period: p })}
                options={[
                  { value: 'weekly', label: t('settings.budgetPeriodWeekly') },
                  { value: 'fortnightly', label: t('settings.budgetPeriodFortnightly') },
                  { value: 'monthly', label: t('settings.budgetPeriodMonthly') },
                ]}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-muted-foreground">
                {t('settings.budgetPeriodHint')}
              </div>
            </div>

            {draft.budget_period === 'fortnightly' && (
              <label className="block text-xs">
                <span className="text-muted-foreground">
                  {t('settings.payAnchorLabel')}
                </span>
                <input
                  type="date"
                  value={draft.pay_anchor_date ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, pay_anchor_date: e.target.value || null })
                  }
                  className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  {t('settings.payAnchorHint')}
                </span>
              </label>
            )}
          </div>
        )}
      </div>
      <button type="button" onClick={() => draft && saveProfile.mutate(draft)} disabled={!ws || !draft || saveProfile.isPending || (draft.budget_period === 'fortnightly' && !draft.pay_anchor_date) || ((draft.default_pay_frequency === 'weekly' || draft.default_pay_frequency === 'fortnightly') && !draft.default_pay_day_of_week)} className="min-h-[44px] w-full rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
        {saveProfile.isPending ? '…' : t('common.save')}
      </button>
    </PageLayout>
  );
}

export function SettingsAppearancePage() {
  const { t, i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  const { theme, setTheme } = useTheme();
  const { locale, translateMerchants, setLocale, setTranslateMerchants } = useUserPreferences();
  const themeOptions: { value: Theme; label: string }[] = [
    { value: 'light', label: zh ? '宝石蓝' : 'Sapphire light' },
    { value: 'dark', label: zh ? '石墨深色' : 'Graphite dark' },
    { value: 'system', label: zh ? '跟随系统' : 'Follow system' },
  ];
  return (
    <PageLayout title={zh ? '外观与语言' : 'Appearance and language'} backTo="/settings">
      <section className="space-y-3 rounded-2xl border bg-card p-4" aria-labelledby="appearance-theme-title">
        <h2 id="appearance-theme-title" className="font-medium">{zh ? '主题外观' : 'Theme appearance'}</h2>
        <p className="text-xs text-muted-foreground">{zh ? '仅保存在此设备，跟随系统会自动切换。' : 'Saved on this device. Follow system switches automatically.'}</p>
        <div role="radiogroup" aria-labelledby="appearance-theme-title" className="grid gap-2">
          {themeOptions.map(option => <label key={option.value} className={`flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 text-left text-sm font-medium focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background ${theme === option.value ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:bg-muted'}`}>
            <input type="radio" name="appearance-theme" value={option.value} checked={theme === option.value} aria-checked={theme === option.value} onChange={() => setTheme(option.value)} className="sr-only" />
            <span>{option.label}</span><span aria-hidden="true" className={`h-4 w-4 rounded-full border ${theme === option.value ? 'border-[5px] border-primary' : 'border-muted-foreground'}`} />
          </label>)}
        </div>
      </section>
      <section className="space-y-3 rounded-2xl border bg-card p-4">
        <h2 className="font-medium">{t('settings.language')}</h2>
        <Segmented<'en' | 'zh'> value={locale === 'zh' ? 'zh' : 'en'} onChange={(next) => {
          if (setLocale.isPending) return;
          setLocale.mutate(next, {
            onSuccess: () => toast.success(t('common.saved')),
            onError: (err) => toast.error(t('common.saveFailed', { error: err instanceof Error ? err.message : '?' })),
          });
        }} options={[{ value: 'en', label: t('settings.english') }, { value: 'zh', label: t('settings.chinese') }]} />
      </section>
      <button type="button" onClick={() => setTranslateMerchants.mutate(!translateMerchants, { onError: (err) => toast.error(t('common.saveFailed', { error: err instanceof Error ? err.message : '?' })) })} disabled={setTranslateMerchants.isPending} aria-pressed={translateMerchants} className="flex min-h-[44px] w-full items-center justify-between gap-4 rounded-2xl border bg-card p-4 text-left hover:bg-muted disabled:opacity-50">
        <span><span className="block font-medium">{t('settings.translateMerchants')}</span><span className="mt-1 block text-xs text-muted-foreground">{t('settings.translateMerchantsHint')}</span></span>
        <span className="shrink-0 rounded-full border px-3 py-1 text-xs">{translateMerchants ? (zh ? '已开启' : 'On') : (zh ? '已关闭' : 'Off')}</span>
      </button>
    </PageLayout>
  );
}

export function SettingsNotificationsPage() {
  const { i18n } = useTranslation();
  return <PageLayout title={i18n.language.startsWith('zh') ? '通知与提醒' : 'Notifications and reminders'} backTo="/settings"><NotificationsSection /></PageLayout>;
}

export function SettingsDataPage() {
  const { t, i18n } = useTranslation();
  return <PageLayout title={i18n.language.startsWith('zh') ? '数据整理' : 'Data organization'} backTo="/settings">
    <PageLink to="/categories" icon={Tags} title={t('settings.categories')} description={t('settings.categoriesHint')} />
    <PageLink to="/aliases" icon={SlidersHorizontal} title={t('settings.aliases')} description={t('settings.aliasesHint')} />
  </PageLayout>;
}

export function SettingsAboutPage() {
  const { t, i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  const clearWs = useWorkspaceStore((s) => s.clear);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  async function handleSignOut() {
    setSigningOut(true);
    clearWs();
    qc.clear();
    sessionStorage.removeItem('pending-invite');
    try { await signOut(); } catch { /* Local session state has already been cleared. */ }
    navigate('/auth', { replace: true });
  }
  return <PageLayout title={zh ? '关于与账号' : 'About and account'} backTo="/settings">
    <section className="space-y-2 rounded-2xl border bg-card p-4">
      <h2 className="font-semibold">Spent Tracker</h2>
      <p className="text-sm">v{__APP_VERSION__}</p>
      <p className="text-xs text-muted-foreground">{new Date(__APP_BUILD_TIME__).toLocaleString(zh ? 'zh-CN' : 'en-AU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
    </section>
    <button type="button" disabled={signingOut} onClick={() => void handleSignOut()} className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm text-destructive disabled:opacity-50"><LogOut className="h-4 w-4" />{t('common.signOut')}</button>
  </PageLayout>;
}

function countryLabel(c: Country, t: (k: string) => string): string {
  switch (c) {
    case 'AU': return t('settings.countryAU');
    case 'US': return t('settings.countryUS');
    case 'UK': return t('settings.countryUK');
    case 'CN': return t('settings.countryCN');
    default: return t('settings.countryOther');
  }
}

function dowLabel(d: number, t: (k: string) => string): string {
  const keys = ['advisor.dowMon', 'advisor.dowTue', 'advisor.dowWed',
    'advisor.dowThu', 'advisor.dowFri', 'advisor.dowSat', 'advisor.dowSun'];
  return t(keys[d - 1] ?? 'advisor.dowMon');
}

